import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/*
 * This file is part of huoxin/filter-rule-manager.
 *
 * Copyright (c) 2026 huoxin.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import extractText from 'flarum/common/utils/extractText';
function escapeHtml(str) {
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
export class FilterEngine {
  constructor() {
    _defineProperty(this, "rulesets", []);
    _defineProperty(this, "providers", {});
    _defineProperty(this, "templates", {});
    _defineProperty(this, "displayModes", {});
    _defineProperty(this, "activeAlerts", []);
    _defineProperty(this, "currentRunAlerts", void 0);
    _defineProperty(this, "blockResults", []);
    _defineProperty(this, "debounceTimer", null);
    _defineProperty(this, "hasAlerts", false);
    _defineProperty(this, "_lastStateKey", null);
    _defineProperty(this, "_subscribers", []);
  }
  /**
   * Subscribe to alert-state changes (active/block result mutations).
   * Returns an unsubscribe function. Used by FilterRulePopupDispatcher to drive
   * toast and modal display modes from a single source of truth.
   */
  subscribe(callback) {
    if (typeof callback !== 'function') return () => {};
    this._subscribers.push(callback);
    return () => {
      this._subscribers = this._subscribers.filter(cb => cb !== callback);
    };
  }
  _notify() {
    for (const cb of this._subscribers) {
      try {
        cb({
          activeAlerts: this.activeAlerts,
          blockResults: this.blockResults
        });
      } catch (e) {
        console.error('[filter-rule-manager] subscriber threw', e);
      }
    }
  }

  /**
   * Register a rule provider. Methods consulted:
   *
   *   Forum bundle:
   *     - getSupportedTypes(): string[]
   *     - evaluate(type, content, config): tokens | null
   *
   *   Admin bundle:
   *     - getSupportedTypes(): string[]
   *     - getTypeLabels(): { [type]: string }            (optional)
   *     - getConfigComponent(type): MithrilComponentClass | null   (optional)
   *
   * If a provider doesn't supply `getConfigComponent` (or it returns null),
   * RuleBuilder renders the generic JSON textarea for that rule.
   *
   * The same provider name may be registered in both bundles — each bundle
   * has its own engine instance, so the forum-side object can implement
   * `evaluate` while the admin-side object implements the UI hooks.
   */
  registerProvider(name, provider) {
    this.providers[name] = provider;
  }

  /**
   * Look up a registered provider by name. Returns null if unknown.
   * Used by the admin RuleBuilder to find a provider's `getConfigComponent`.
   */
  getProvider(name) {
    return this.providers[name] || null;
  }

  /**
   * Register a display template component.
   */
  registerTemplate(name, component, settingsComponent) {
    if (settingsComponent === void 0) {
      settingsComponent = null;
    }
    this.templates[name] = {
      component: component,
      settingsComponent: settingsComponent
    };
  }

  /**
   * Get a registered display template component.
   */
  getTemplate(name) {
    return this.templates[name] ? this.templates[name].component : null;
  }

  /**
   * Get a registered display template settings component.
   */
  getTemplateSettingsComponent(name) {
    return this.templates[name] ? this.templates[name].settingsComponent : null;
  }

  /**
   * Register a display mode placement option.
   * @param {string} key - The unique identifier for the mode
   * @param {string} translationKey - The translation key for the UI label
   */
  registerDisplayMode(key, translationKey) {
    this.displayModes[key] = translationKey;
  }

  /**
   * Get all registered display modes.
   */
  getDisplayModes() {
    return this.displayModes;
  }
  getRegisteredFrontendTypes() {
    const types = [];

    // `app` is available globally.

    for (const _ref of Object.entries(this.providers)) {
      var _ref2 = _slicedToArray(_ref, 2);
      const name = _ref2[0];
      const provider = _ref2[1];
      if (typeof provider.getSupportedTypes !== 'function') continue;
      const supported = provider.getSupportedTypes();
      const labels = typeof provider.getTypeLabels === 'function' ? provider.getTypeLabels() : {};
      let providerLabel = name;
      if (typeof provider.getProviderLabel === 'function') {
        providerLabel = provider.getProviderLabel();
      } else if (app && app.translator) {
        const transKey = "huoxin-filter-rule-manager.admin.providers.".concat(name);
        const translated = app.translator.trans(transKey);
        const isTranslated = Array.isArray(translated) ? translated[0] !== transKey : translated !== transKey;
        if (isTranslated) providerLabel = String(Array.isArray(translated) ? translated[0] : translated);
      }
      supported.forEach(type => {
        types.push({
          provider: name,
          providerLabel,
          type,
          label: labels[type] || type
        });
      });
    }
    return types;
  }
  loadRulesets(rulesets) {
    this.rulesets = rulesets || [];
  }
  start() {
    // Perform an initial evaluation immediately on start
    this.evaluate();
  }
  debouncedEvaluate() {
    if (this.debounceTimer) {
      window.clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = window.setTimeout(() => {
      this.evaluate();
      this.debounceTimer = null;
    }, 300);
  }
  stop() {
    if (this.debounceTimer) {
      window.clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.activeAlerts = [];
    this.blockResults = [];
    this.hasAlerts = false;
    this._lastStateKey = null;
    this._notify();
  }
  setBlockResults(filterRules) {
    this.blockResults = (filterRules || []).map(alert => {
      const tokens = alert.tokens || {};
      let displaySettings = alert.display_settings || {};
      if (typeof displaySettings.title === 'string') {
        displaySettings = _objectSpread(_objectSpread({}, displaySettings), {}, {
          title: this.interpolate(displaySettings.title, tokens)
        });
      }
      return {
        interventionType: alert.intervention_type,
        displayMode: alert.display_mode,
        message: alert.message,
        tokens,
        displaySettings,
        isBlock: true
      };
    });
    this.hasAlerts = this.activeAlerts.length > 0 || this.blockResults.length > 0;
    this._notify();
    if (typeof m !== 'undefined') m.redraw();
  }
  clearBlockResults() {
    this.blockResults = [];
    this.hasAlerts = this.activeAlerts.length > 0;
    this._notify();
    if (typeof m !== 'undefined') m.redraw();
  }
  evaluate() {
    const application = app;
    const composer = application && application.composer;
    if (!composer || typeof composer.isVisible !== 'function' || !composer.isVisible()) return;
    const content = composer.fields && composer.fields.content && composer.fields.content() || '';
    const title = composer.fields && composer.fields.title && composer.fields.title() || '';
    const stateKey = "".concat(title, "\n\n").concat(content);
    if (stateKey === this._lastStateKey) return;
    this._lastStateKey = stateKey;
    const activeAlerts = [];
    this.currentRunAlerts = activeAlerts;
    for (const ruleset of this.rulesets) {
      if (!this.scopeMatches(ruleset, composer, application)) continue;
      let targetContent = content;
      if (ruleset.evaluateTitle !== false && title) {
        targetContent = title + '\n\n' + content;
      }
      const tokens = this.evaluateRuleset(ruleset, targetContent);
      if (tokens !== null) {
        let displaySettings = ruleset.displaySettings || {};
        if (typeof displaySettings.title === 'string') {
          displaySettings = _objectSpread(_objectSpread({}, displaySettings), {}, {
            title: this.interpolate(displaySettings.title, tokens)
          });
        }
        activeAlerts.push({
          ruleset,
          tokens,
          message: this.interpolate(ruleset.message, tokens),
          displaySettings
        });
        if (ruleset.blockCascade) break;
      }
    }
    const changed = this.alertsChanged(this.activeAlerts, activeAlerts);
    this.activeAlerts = activeAlerts;
    this.currentRunAlerts = undefined;
    this.hasAlerts = this.activeAlerts.length > 0 || this.blockResults.length > 0;
    if (changed) {
      this._notify();
      if (typeof m !== 'undefined') m.redraw();
    }
  }
  evaluateRuleset(ruleset, content) {
    const ast = typeof ruleset.compiledAst === 'function' ? ruleset.compiledAst() : ruleset.compiled_ast;
    if (!ast) return null;
    return this.evaluateAST(ast, content, ruleset);
  }
  evaluateAST(node, content, ruleset) {
    if (!node) return null;
    if (node.type === 'logical') {
      const left = this.evaluateAST(node.left, content, ruleset);
      if (node.operator === 'OR') {
        const evaluateAll = typeof ruleset.evaluateAllRules === 'function' ? ruleset.evaluateAllRules() : ruleset.evaluateAllRules;
        if (left !== null && !evaluateAll) return left;
        const right = this.evaluateAST(node.right, content, ruleset);
        if (left !== null && right !== null) return this.mergeResults([left, right]);
        return left !== null ? left : right;
      }
      if (node.operator === 'AND') {
        if (left === null) return null;
        const right = this.evaluateAST(node.right, content, ruleset);
        if (right === null) return null;
        return this.mergeResults([left, right]);
      }
    }
    if (node.type === 'not') {
      const result = this.evaluateAST(node.node, content, ruleset);
      return result === null ? {} : null;
    }
    if (node.type === 'rule') {
      return this.evaluateRuleNode(node, content);
    }
    return null;
  }
  mergeResults(results) {
    let merged = {};
    for (const r of results) {
      if (r !== null) {
        for (const _ref3 of Object.entries(r)) {
          var _ref4 = _slicedToArray(_ref3, 2);
          const key = _ref4[0];
          const val = _ref4[1];
          if (typeof merged[key] === 'string' && typeof val === 'string') {
            const existing = merged[key].split(',').map(s => s.trim());
            const added = val.split(',').map(s => s.trim());
            merged[key] = Array.from(new Set([...existing, ...added])).join(', ');
          } else {
            merged[key] = val;
          }
        }
      }
    }
    return merged;
  }
  evaluateRuleNode(node, content) {
    if (!node.provider) return null;
    const provider = this.providers[node.provider];
    if (!provider || typeof provider.evaluate !== 'function') return null;
    if (!provider.getSupportedTypes().includes(node.ruleType)) return null;
    let result = null;
    try {
      const isObject = typeof node.value === 'object' && node.value !== null && !Array.isArray(node.value);
      let config = isObject ? _objectSpread(_objectSpread({}, node.value), {}, {
        operator: node.operator
      }) : {
        operator: node.operator,
        value: node.value
      };
      result = provider.evaluate(node.ruleType, content, config);
    } catch (e) {
      console.error("[filter-rule-manager] rule ".concat(String(node.provider), "/").concat(String(node.ruleType), " threw"), e);
      return null;
    }
    return result;
  }
  scopeMatches(ruleset, composer, application) {
    if (!composer) return false;
    let isPrivate = false;
    let tagIds = [];
    let discussion = null;
    if (composer.body && composer.body.attrs) {
      if (composer.body.attrs.post) {
        discussion = composer.body.attrs.post.discussion();
      } else if (composer.body.attrs.discussion) {
        discussion = composer.body.attrs.discussion;
      }
    }
    const safeComposer = composer;
    if (discussion) {
      const recipientUsers = discussion.recipientUsers && discussion.recipientUsers();
      const recipientGroups = discussion.recipientGroups && discussion.recipientGroups();
      const isPrivateAttr = discussion.isPrivate && discussion.isPrivate() || discussion.isPrivateDiscussion && discussion.isPrivateDiscussion() || discussion.attribute('isPrivate') || discussion.attribute('is_private');
      isPrivate = !!isPrivateAttr || recipientUsers && recipientUsers.length > 0 || recipientGroups && recipientGroups.length > 0;
      tagIds = discussion.tags && discussion.tags() ? discussion.tags().map(t => t.id()) : [];
    } else {
      var _safeComposer$fields, _safeComposer$fields2, _safeComposer$fields3, _safeComposer$fields4, _safeComposer$fields5, _safeComposer$fields6;
      const resolveField = val => typeof val === 'function' ? val() : val;
      let recipientUsers = resolveField((_safeComposer$fields = safeComposer.fields) == null ? void 0 : _safeComposer$fields.recipientUsers) || [];
      let recipientGroups = resolveField((_safeComposer$fields2 = safeComposer.fields) == null ? void 0 : _safeComposer$fields2.recipientGroups) || [];
      const recipientsField = resolveField((_safeComposer$fields3 = safeComposer.fields) == null ? void 0 : _safeComposer$fields3.recipients);
      const recipientsArray = recipientsField && typeof recipientsField.toArray === 'function' ? recipientsField.toArray() : Array.isArray(recipientsField) ? recipientsField : [];
      const fieldsIsPrivate = resolveField((_safeComposer$fields4 = safeComposer.fields) == null ? void 0 : _safeComposer$fields4.isPrivate);
      const hasRecipients = recipientUsers.length > 0 || recipientGroups.length > 0 || recipientsArray.length > 0;
      isPrivate = !!fieldsIsPrivate || hasRecipients;
      tagIds = resolveField((_safeComposer$fields5 = safeComposer.fields) == null ? void 0 : _safeComposer$fields5.tags) ? resolveField((_safeComposer$fields6 = safeComposer.fields) == null ? void 0 : _safeComposer$fields6.tags).map(t => t.id()) : [];
    }
    switch (ruleset.scopeType) {
      case 'global':
        return true;
      case 'normal_post':
        return !isPrivate;
      case 'private_post':
        return isPrivate;
      case 'tag':
        if (!ruleset.scopeTagIds || ruleset.scopeTagIds.length === 0) return false;
        const targetTags = ruleset.scopeTagIds.map(String);
        return tagIds.map(String).some(id => targetTags.includes(id));
      default:
        return false;
    }
  }

  /**
   * Interpolate {{token}} placeholders.
   *
   * Token VALUES are HTML-escaped because they often come from user-controlled
   * post content (e.g. the matched regex substring). The template itself is
   * admin-authored and therefore trusted, so callers can safely render the
   * result with `m.trust(...)` for admin-supplied formatting like <br>.
   */
  interpolate(template, tokens) {
    if (!template) return '';
    let strTemplate = Array.isArray(template) ? template.join('') : String(template);

    // If the template strictly matches a Flarum translation key format (e.g. namespace.key)
    // we translate it first and inject the tokens natively into the translator.
    if (/^[a-zA-Z0-9\-_]+(?:\.[a-zA-Z0-9\-_]+)+$/.test(strTemplate) && typeof app !== 'undefined' && app.translator && app.translator.trans) {
      try {
        const translated = app.translator.trans(strTemplate, tokens || {});
        if (typeof translated === 'string' && translated !== strTemplate && translated !== '') {
          strTemplate = translated;
        } else if (Array.isArray(translated) && translated.length > 0) {
          const textOnly = extractText(translated);
          if (textOnly && textOnly !== strTemplate) {
            strTemplate = textOnly;
          }
        }
      } catch (e) {
        // Fallback to original string on ICU parse error
      }
    }
    return strTemplate.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      if (!tokens || !Object.prototype.hasOwnProperty.call(tokens, key)) return match;
      return escapeHtml(tokens[key]);
    });
  }
  alertsChanged(oldAlerts, newAlerts) {
    if (oldAlerts.length !== newAlerts.length) return true;
    for (let i = 0; i < oldAlerts.length; i++) {
      if (oldAlerts[i].ruleset.id !== newAlerts[i].ruleset.id) return true;
      if (oldAlerts[i].message !== newAlerts[i].message) return true;
    }
    return false;
  }
}
const filterEngine = new FilterEngine();
export default filterEngine;
flarum.reg.add('huoxin-filter-rule-manager', 'common/FilterEngine', filterEngine);